import { useState } from "react";
import { registerBook, getBook, setCompleted, setRegisteredDate } from "../api/books";
import useBookSearch from "../hooks/useBookSearch";
import { formatDate } from "../utils/format";
import { GENRE_LIST } from "../data/constants";
import { track } from "../utils/analytics";
import "./AddModal.css";

/**
 * 책 등록.
 *
 * 예전에는 검색 결과를 누르는 순간 확인 없이 등록됐다. 잘못 누르면
 * 서재에 남고, 지우려면 책 화면까지 들어가야 했다. 누르는 것과
 * 등록하는 것을 나눈다.
 *
 * 상태를 고르는 자리를 함께 둔다. 여태 무엇을 눌러도 '읽는 중' 으로
 * 들어갔는데, 완독한 책의 49%가 시작일과 완료일이 같은 날이다 —
 * 절반은 이미 읽은 책을 기록하려던 것이었고 그때마다 책 화면에서
 * 완독을 다시 눌러야 했다.
 *
 * wantToRead 로 열면 '읽고 싶어요' 가 먼저 골라져 있다. 들어온 문으로
 * 뜻을 짐작하되, 바꿀 수 있게 둔다.
 */

const STATES = [
  { key: "wish",    label: "읽고 싶어요",  desc: "궤도 밖에서 기다립니다" },
  { key: "reading", label: "읽는 중이에요", desc: "궤도를 돌기 시작합니다" },
  { key: "done",    label: "다 읽었어요",  desc: "행성에 자리를 잡습니다" },
];

const AddModal = ({ onClose, onAddBook, onBookClick, currentUser, allBooks = [], wantToRead = false, prefill = null }) => {
  const [query, setQuery] = useState("");
  const { results, loading } = useBookSearch(query);

  /** 고른 책. null 이면 아직 1단계(고르기)다. */
  const [picked, setPicked] = useState(prefill);
  const [state, setState] = useState(wantToRead ? "wish" : "reading");
  /* 장르는 라벨이 아니라 감정 좌표의 축을 정하는 값이다. 검색 API 가
     장르를 안 주면 '기타' 로 들어가, 182권 중 77권(42%)이 그렇게 쌓였고
     그만큼이 잘못된 축 위에 찍히고 있었다. 고르지 않으면 등록하지 않는다. */
  const [genre, setGenre] = useState(
    prefill && GENRE_LIST.includes(prefill.genre) ? prefill.genre : null
  );
  const [date, setDate] = useState(() => formatDate(new Date()));
  const [saving, setSaving] = useState(false);

  /** 이미 MUROO 에 있는 책이면 몇 명이 담았는지. 제목·저자로 맞춘다. */
  const readersOf = (item) => {
    const hit = (allBooks || []).find((b) => b.title === item.title && b.author === item.author);
    return hit ? Object.keys(hit.registeredBy || {}).length : 0;
  };

  /** API 가 준 장르가 고를 수 있는 값이면 미리 골라둔다. '기타' 나 빈 값,
      영어 분류명(Organizational behavior 같은 것이 실제로 들어온다)은 버린다. */
  const pick = (item) => {
    setPicked(item);
    setGenre(GENRE_LIST.includes(item.genre) ? item.genre : null);
  };

  const commit = async () => {
    const uid = currentUser?.uid;
    if (!uid || !picked || !genre) return;
    setSaving(true);
    try {
      // (title, author) unique 제약 + upsert 라 두 명이 동시에 담아도
      // 책이 중복 생성되지 않는다.
      const { id, isNewToMuroo } = await registerBook(
        {
          title: picked.title, author: picked.author, genre,
          cover: picked.cover || null, description: picked.description || null,
          isbn: picked.isbn || null,
        },
        uid,
        { wantToRead: state === "wish" }
      );

      /* 담아둔 책에는 날짜를 넣지 않는다 — 아직 펴지 않았다. */
      if (state !== "wish") {
        await setRegisteredDate(id, uid, date);
        /* 이미 읽은 책이면 완료일도 같은 날로 둔다. 실제로 절반이
           그렇게 쓰고 있어서, 여기서 넣어두면 다시 누를 일이 없다. */
        if (state === "done") await setCompleted(id, uid, date);
      }

      const book = await getBook(id);
      onAddBook(book ?? {
        id, title: picked.title, author: picked.author,
        cover: picked.cover || null, genre,
        readers: 1, registeredBy: { [uid]: date },
      }, isNewToMuroo);
      /* 어느 상태로 담았는지만 센다. 제목은 보내지 않는다. */
      track("book_added", { state, from_search: !prefill });
      onClose();
      /* 담아둔 책은 책 화면으로 끌고 가지 않는다 — 거기서 할 일이 없고,
         목록에 남아야 이어서 담을 수 있다. */
      if (state !== "wish" && onBookClick) onBookClick(id);
    } catch (e) {
      console.error("등록 실패:", e.message);
      setSaving(false);
    }
  };

  const seen = picked ? readersOf(picked) : 0;

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <button className="modal-close" onClick={onClose}>✕</button>

        {/* 두 걸음임을 미리 알린다. 누르는 것이 곧 등록이 아니라는 것을
            여기서 말해두면 결과가 놀랍지 않다.
            책 화면에서 열었을 때는 고를 것이 없어 한 걸음뿐이다. */}
        {!prefill && (
        <div className="am-steps">
          <span className={picked ? "am-step" : "am-step on"}>1 책 고르기</span>
          <span className="am-step-sep">→</span>
          <span className={picked ? "am-step on" : "am-step"}>2 상태 정하기</span>
        </div>
        )}

        {!picked ? (
          <>
            <h3>{wantToRead ? "🔖 읽고 싶은 책 담기" : "📚 책 등록하기"}</h3>
            <div className="am-hint">먼저 책을 고른 뒤, 다음 단계에서 상태를 정합니다</div>
            <input
              className="search-input"
              placeholder="책 제목 또는 저자 검색..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoFocus
            />
            {loading && <div className="empty-state empty-state--compact">🔍 검색 중...</div>}
            {!loading && query.length >= 2 && results.length === 0 && (
              <div className="empty-state">검색 결과가 없어요 🔍</div>
            )}
            {!loading && results
              .filter((item, i, arr) => arr.findIndex((x) => x.title === item.title && x.author === item.author) === i)
              .map((item) => {
                const n = readersOf(item);
                return (
                  <div key={item.googleId} className="search-result" onClick={() => pick(item)}>
                    {item.cover
                      ? <img src={item.cover} alt="" className="search-result-cover" />
                      : <div className="search-cover" style={{ background: "#1a1a2e" }}>📖</div>}
                    <div>
                      <div className="search-title">{item.title}</div>
                      <div className="search-meta">
                        {item.author}{item.publishedDate ? ` · ${item.publishedDate.slice(0, 4)}` : ""}
                      </div>
                      {/* 1명일 때는 말하지 않는다. 지금 대부분의 책이 1명이라
                          낱개로 세면 오히려 텅 빈 것을 확인시켜 준다. */}
                      {n >= 2 && <div className="am-seen">이 우주에서 {n}명이 담았어요</div>}
                    </div>
                  </div>
                );
              })}
            {!query && <div className="empty-state">책 제목이나 저자 이름을 입력해보세요 ✨</div>}
          </>
        ) : (
          <>
            <h3>이 책, 지금 어떤 상태인가요?</h3>
            <div className="am-hint">나중에 언제든 바꿀 수 있어요</div>

            <div className="am-picked">
              {picked.cover
                ? <img src={picked.cover} alt="" className="am-picked-cover" />
                : <div className="search-cover" style={{ background: "#1a1a2e" }}>📖</div>}
              <div>
                <div className="search-title">{picked.title}</div>
                <div className="search-meta">{picked.author}</div>
                {seen >= 2 && <div className="am-seen">이 우주에서 {seen}명이 담았어요</div>}
              </div>
            </div>

            <div className="am-states">
              {STATES.map((s) => (
                <button
                  key={s.key}
                  className={state === s.key ? "am-state on" : "am-state"}
                  onClick={() => setState(s.key)}
                  aria-pressed={state === s.key}
                >
                  <span className="am-state-l">{s.label}</span>
                  <span className="am-state-d">{s.desc}</span>
                </button>
              ))}
            </div>

            <div className="am-genre">
              <div className="am-genre-t">
                장르
                {/* 왜 묻는지 말해준다. 안 그러면 분류를 시키는 것처럼 보인다. */}
                <span className="am-genre-why">감정 좌표의 축이 장르에 따라 달라져요</span>
              </div>
              <div className="am-genre-grid">
                {GENRE_LIST.map((g) => (
                  <button
                    key={g}
                    className={genre === g ? "am-genre-b on" : "am-genre-b"}
                    onClick={() => setGenre(g)}
                    aria-pressed={genre === g}
                  >{g}</button>
                ))}
              </div>
            </div>

            {/* 담아둔 책에는 날짜를 묻지 않는다 — 아직 펴지 않았다. */}
            {state !== "wish" && (
              <label className="am-date">
                <span>{state === "done" ? "읽은 날" : "읽기 시작한 날"}</span>
                <input
                  type="date"
                  value={date.replaceAll(".", "-")}
                  max={formatDate(new Date()).replaceAll(".", "-")}
                  onChange={(e) => e.target.value && setDate(e.target.value.replaceAll("-", "."))}
                />
              </label>
            )}

            <div className="am-btns">
              {/* 돌아갈 길을 늘 열어둔다. 책 화면에서 열었을 때는 고른 적이
                  없으니 '다시 고르기' 가 갈 곳이 없다 — ✕ 로 닫는다. */}
              {!prefill && (
                <button className="am-back" onClick={() => setPicked(null)}>← 다시 고르기</button>
              )}
              <button className="am-go" onClick={commit} disabled={saving || !genre}>
                {saving ? "등록 중…" : genre ? "내 행성에 등록하기" : "장르를 골라주세요"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AddModal;
